import { groupBy } from '@aline/common';
import { format, isAfter, parseISO, startOfDay, subDays } from 'date-fns';
import type { Apollo } from 'src/gql/generated';

export function normalizeStreamingText(text: string): { isPulsing: boolean; text: string } {
  // Check for incomplete markdown link at end using regex
  const incompleteLink = /(?<!\\)(\[[^\]]*$|\[[^\]]*\]\([^)]*$)/;
  const match = text.match(incompleteLink);

  /**
   * If there is an incomplete markdown link, we need to return the text up to
   * the link and set the isPulsing flag to true.
   */
  if (match) {
    return {
      isPulsing: true,
      text: text.substring(0, match.index),
    };
  }

  return {
    isPulsing: false,
    text,
  };
}

export type Thread = Pick<Apollo.AgentThread, 'id' | 'title' | 'createdAt'>;

/**
 * Groups threads by date ranges (today, last 7 days, last 30 days, and older)
 */
export function groupThreadsByDate(now: Date, threads: Thread[]) {
  const todayStart = startOfDay(now);
  const sevenDaysAgo = subDays(now, 7);
  const thirtyDaysAgo = subDays(now, 30);
  const {
    beforeCurrentMonthThreads = [],
    last7DaysThreads = [],
    last30DaysThreads = [],
    todayThreads = [],
  } = groupBy(thread => {
    const createdAt = parseISO(thread.createdAt);
    if (isAfter(createdAt, todayStart)) return 'todayThreads';
    if (isAfter(createdAt, sevenDaysAgo)) return 'last7DaysThreads';
    if (isAfter(createdAt, thirtyDaysAgo)) return 'last30DaysThreads';
    return 'beforeCurrentMonthThreads';
  }, threads);
  const olderThreadsByMonth = beforeCurrentMonthThreads.reduce<Record<string, Thread[]>>(
    (acc, thread) => {
      const createdAt = parseISO(thread.createdAt);
      const monthYear = format(createdAt, 'MMMM yyyy');
      if (!acc[monthYear]) acc[monthYear] = [];
      acc[monthYear].push(thread);
      return acc;
    },
    {}
  );

  return {
    last7DaysThreads,
    last30DaysThreads,
    olderThreadsByMonth,
    todayThreads,
  };
}
